import { Box, Typography, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAppContext } from "../../../../2_ksbHub/context/appContext";
import { useBszFinderContext } from "../../context/bszContext";
import { useEffect, useRef, useState } from "react";

export default function BszSlot({
  slot,
  type,
  onReserve,
  serverNow, // ✅ important
}) {
  const theme = useTheme();

  const { currentUser, isAStudent, isATeacher } = useAppContext();
  const { isBszAdmin } = useBszFinderContext();

  const [animate, setAnimate] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  /**
   * =========================================================
   * ✅ BASIC STATE
   * =========================================================
   */

  const hasReservation = !!slot.reservedBy;
  const isExpired = hasReservation && timeLeft === 0;

  const isReserved = hasReservation && !isExpired;
  const isMine = isReserved && slot.reservedBy?.id === currentUser?.id;

  /**
   * =========================================================
   * ✅ STATUS FLAGS
   * =========================================================
   */

  const isExpiringSoon = timeLeft !== null && timeLeft > 0 && timeLeft < 300; // <5 min

  /**
   * =========================================================
   * ✅ ROLE LOGIC
   * =========================================================
   */
  const isAllowed =
    (type === "student" && isAStudent) || (type === "teacher" && isATeacher);

  const isClickable = (!isReserved && isAllowed) || isBszAdmin || isMine;

  /**
   * =========================================================
   * ✅ SERVER TIME SYNC
   * =========================================================
   */

  const offsetRef = useRef(null);

  useEffect(() => {
    offsetRef.current = Date.now() - serverNow;
  }, [serverNow]);
  /**
   * =========================================================
   * ✅ TIMER
   * =========================================================
   */
  useEffect(() => {
    if (!slot?.endTime) {
      setTimeLeft(null);
      return;
    }

    const update = () => {
      const nowSynced = Date.now() - offsetRef.current;

      const diffSeconds = Math.floor((slot.endTime - nowSynced) / 1000);

      const diff = Math.max(0, diffSeconds);

      setTimeLeft(diff);
    };

    update();
    const interval = setInterval(update, 1000);

    return () => clearInterval(interval);
  }, [slot?.endTime]);

  /**
   * =========================================================
   * ✅ COLORS
   * =========================================================
   */
  const baseColor = isMine
    ? theme.palette.eventManager.slot.mine
    : isReserved
      ? theme.palette.eventManager.slot.reserved
      : theme.palette.eventManager.slot.free;

  const backgroundColor = isExpiringSoon
    ? theme.palette.warning.main
    : baseColor;

  const textColor = theme.palette.eventManager.textOnSlot;

  /**
   * =========================================================
   * ✅ ANIMATION
   * =========================================================
   */
  useEffect(() => {
    setAnimate(true);
    const t = setTimeout(() => setAnimate(false), 250);
    return () => clearTimeout(t);
  }, [slot.reservedBy?.id]);

  /**
   * =========================================================
   * ✅ FORMAT TIME
   * =========================================================
   */
  const formatTime = (seconds) => {
    if (seconds == null) return "";

    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;

    return `${min}:${sec.toString().padStart(2, "0")}`;
  };

  /**
   * =========================================================
   * ✅ DISPLAY
   * =========================================================
   */
  const displayText =
    !isReserved || isExpired
      ? "frei"
      : `${slot.reservedBy.name} (${formatTime(timeLeft)})`;

  /**
   * =========================================================
   * ✅ CLICK
   * =========================================================
   */
  const handleClick = () => {
    if (!isClickable) return;
    onReserve(slot.id, slot);
  };

  /**
   * =========================================================
   * ✅ RENDER
   * =========================================================
   */
  return (
    <Box
      onClick={handleClick}
      sx={{
        px: 1.6,
        py: 0.7,
        borderRadius: 999,
        backgroundColor,
        color: textColor,
        border: isMine ? `2px solid ${theme.palette.common.white}` : "none",
        opacity: isClickable ? 1 : 0.5,
        cursor: isClickable ? "pointer" : "not-allowed",
        fontSize: "0.75rem",

        display: "flex",
        alignItems: "center",
        gap: 0.5,

        transform: animate ? "scale(1.08)" : "scale(1)",
        transition: "all 0.2s ease",

        "&:hover": isClickable
          ? {
              transform: "translateY(-1px)",
              boxShadow: `0 2px 6px ${theme.palette.primary.main}`,
            }
          : {},
      }}
    >
      {/* ✅ TEXT */}
      <Typography variant="caption" sx={{ fontWeight: 500 }}>
        {displayText}
      </Typography>

      {/* ✅ EXPIRING BADGE */}
      {isExpiringSoon && !isMine && (
        <Chip
          label="ending soon"
          size="small"
          sx={{
            height: 18,
            fontSize: "0.6rem",
            backgroundColor: theme.palette.warning.dark,
            color: "#fff",
          }}
        />
      )}
    </Box>
  );
}
